import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';

import { authTokenAtom } from 'atoms/authAtoms';
import { stageConfigAtom } from 'atoms/Ltool/stageConfigAtoms';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { gearIdAtom } from 'atoms/gearAtom';
import { errorAtom } from 'atoms/error';
import { mappingAtom } from 'atoms/mappingAtoms';
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { useSubmitTargetGuard } from 'utils/hooks/useSubmitTargetGuard';
import { toUserMessage } from 'utils/apiErrorMessage';
import { buildLCalcConditionResult, type LCalcConditionResult } from 'utils/lCalcConditionResult';
import {
    submitFrequencyConversionCalculation,
    submitFrequencyConversionCalculationHev,
} from 'api/submitLtool';
import { LCalcConditionResultAlerts } from './LCalcConditionResultAlerts';

export const FrequencyConversionCalculation: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(stageConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const setError = useSetAtom(errorAtom);
    const mapping = useAtomValue(mappingAtom);
    const { showError, showWarning } = useSnackbar();
    const beginSubmit = useSubmitTargetGuard();

    // HEV は gear 単位（選択 gear の全 unit × 全 condition を計算）
    const isHev = powertrain === 'hev';

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<LCalcConditionResult | null>(null);

    // 計算対象（計算セット / gear / powertrain）が変わったら前回のサマリ表示をクリアする
    // （再計算するまで別対象の結果が残って誤認するのを防ぐ）。
    useEffect(() => {
        setResult(null);
    }, [gearId, powertrain, config]);

    const handleClick = async () => {
        if (isHev && !gearId) {
            showWarning("HEV では Gear を選択してください。");
            return;
        }
        // 送信中に計算セット / gear を切り替えた・L ツールを離れた場合、成功・失敗とも前の対象の応答を出さない。
        const guard = beginSubmit();
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            // 原則 200 + 4 分類サマリ。BE が unit × condition を最後まで処理して分類を返す。
            const summary = isHev
                ? await submitFrequencyConversionCalculationHev(authToken, config, gearId!)
                : await submitFrequencyConversionCalculation(authToken, config);
            if (guard.isStale()) return;
            setResult(buildLCalcConditionResult(summary, mapping));
        } catch (e) {
            // 画面には出さない古い対象の失敗でも、開発者向けの詳細は残す
            console.error("周波数変換の計算に失敗しました:", e);
            if (guard.isStale()) return;
            // 前提エラー（4xx/5xx）時のみ従来のエラー表示。
            // errorAtom は画面を離れた後に書くと誰も消せずに残る（L のセレクタが赤いままになる）ため、mount 中だけ書く。
            if (guard.isMounted()) setError(toUserMessage(e));
            showError(toUserMessage(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                周波数変換の計算
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                登録済みデータをもとに周波数から車速・モーター回転数への変換を計算します。
            </Typography>

            <Button
                variant="contained"
                sx={{ minWidth: 140 }}
                disabled={loading || (isHev && !gearId)}
                startIcon={loading ? <CircularProgress size={18} /> : undefined}
                onClick={handleClick}
            >
                {loading ? '計算中...' : '計算実行'}
            </Button>

            <LCalcConditionResultAlerts result={result} />
        </Box>
    );
};
